import React, { useMemo, useState } from 'react';
import { Calendar, ArrowLeft, ChevronDown, ChevronUp, Clock, Hash } from 'lucide-react';

const BookTimelineSection = ({
  diary = [],
  bookKey,
  bookTitle,
  bookAuthor,
  theme,
  isDarkMode,
  handleNavigate
}) => {
  const pillStyle = { borderRadius: '9999px' };
  const cardStyle = { borderRadius: '24px' };

  const formatReadableDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        const year = parts[0];
        const monthNum = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const malayMonths = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
          'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
        return `${day} ${malayMonths[monthNum]} ${year}`;
      }
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
      }
    } catch (e) { /* ignore */ }
    return dateStr;
  };

  const stripHtml = (html) => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    } catch (e) { return html.replace(/<[^>]+>/g, ''); }
  };

  // Filter entries for this specific book, sorted chronologically
  const entries = useMemo(() => {
    return (diary || [])
      .filter(entry => {
        const entryKey = (entry.title || '').toLowerCase().trim() + '|||' + (entry.author || '').toLowerCase().trim();
        return entryKey === bookKey;
      })
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateA - dateB; // chronological order for timeline
      });
  }, [diary, bookKey]);

  // Group entries by topic for sectioned timeline
  const topicGroups = useMemo(() => {
    const groups = new Map();
    entries.forEach(entry => {
      const topic = entry.topic?.trim() || 'General';
      if (!groups.has(topic)) groups.set(topic, []);
      groups.get(topic).push(entry);
    });
    // Sort groups: 'General' last, rest alphabetically
    const sorted = Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'General') return 1;
      if (b === 'General') return -1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [entries]);

  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (entryId) => {
    setExpandedId(prev => prev === entryId ? null : entryId);
  };

  const getTypeLabel = (type) => type === 'book' ? 'Book' : 'Article';

  return (
    <section className="pt-32 pb-24 px-4 md:px-6 min-h-screen animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="max-w-3xl mx-auto w-full">

        <style>{`
          @keyframes staggerFadeUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .stagger-item { animation: staggerFadeUp 0.6s ease-out both; }
        `}</style>

        {/* Breadcrumb */}
        <div className="mb-8 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] opacity-50">
          <button onClick={() => handleNavigate('diary')} className={`hover:opacity-100 transition-opacity ${theme.text}`}>Bookshelf</button>
          <span className="opacity-30">/</span>
          <span className={`opacity-80 ${theme.text}`}>{bookTitle}</span>
        </div>

        {/* Back Button */}
        <button onClick={() => handleNavigate('diary')}
          className={`group mb-8 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] opacity-50 hover:opacity-100 transition-opacity ${theme.text}`}>
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          <span>Back to Bookshelf</span>
        </button>

        {/* Book Header */}
        <header className="mb-16">
          <div className="flex items-center gap-4 mb-4">
            <div style={pillStyle} className={`inline-flex px-4 py-1.5 border text-[9px] font-bold uppercase tracking-widest items-center gap-2 ${
              isDarkMode ? 'bg-transparent border-stone-800 text-stone-500' : 'bg-transparent border-stone-100 text-stone-400'
            }`}>
              {getTypeLabel(entries[0]?.type)}
            </div>
            {entries.length > 0 && entries[0]?.category && (
              <div style={pillStyle} className={`inline-flex px-4 py-1.5 border text-[9px] font-bold uppercase tracking-widest ${
                isDarkMode ? 'bg-transparent border-stone-800 text-stone-500' : 'bg-transparent border-stone-100 text-stone-400'
              }`}>
                {entries[0].category}
              </div>
            )}
          </div>
          <h1 className={`text-4xl md:text-5xl font-garamond leading-[1.05] tracking-tight ${theme.text}`}>{bookTitle}</h1>
          {bookAuthor && <p className={`text-sm font-semibold uppercase tracking-[0.1em] opacity-40 mt-3 ${theme.text}`}>By {bookAuthor}</p>}
          <p className={`text-[10px] font-bold uppercase tracking-widest opacity-40 mt-4 ${theme.textMuted}`}>
            {entries.length} {entries.length === 1 ? 'reflection' : 'reflections'} · Reading Journey
          </p>
        </header>

        {/* Timeline */}
        {entries.length === 0 ? (
          <div className="text-center py-24 rounded-[32px] border border-dashed border-stone-200 dark:border-stone-800 opacity-60">
            <p className={`font-garamond italic text-xl ${theme.text}`}>No reflections found for this title.</p>
          </div>
        ) : (
          <div className="relative pl-6 md:pl-10">
            {/* Vertical timeline axis */}
            <div className={`absolute top-0 bottom-0 left-[9px] md:left-[11px] w-[1px] ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />

            {topicGroups.map(([topic, groupEntries], groupIndex) => (
              <div key={topic} className="mb-16 last:mb-0">
                {/* Topic Section Header */}
                <div className="stagger-item relative mb-8" style={{ animationDelay: `${groupIndex * 100}ms` }}>
                  {/* Header node on timeline */}
                  <div className={`absolute left-[-15px] md:left-[-29px] -translate-x-1/2 top-2 w-[16px] h-[16px] rounded-full border-2 z-10 flex items-center justify-center ${
                    isDarkMode ? 'bg-stone-900 border-stone-600' : 'bg-white border-stone-300'
                  }`}>
                    <Hash size={8} className={`${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`} />
                  </div>
                  <h3 className={`text-sm font-bold uppercase tracking-[0.25em] ${theme.text}`}>
                    {topic === 'General' ? 'General Reflections' : topic}
                  </h3>
                  <p className={`text-[9px] font-bold uppercase tracking-widest opacity-30 mt-1 ${theme.textMuted}`}>
                    {groupEntries.length} {groupEntries.length === 1 ? 'reflection' : 'reflections'}
                  </p>
                </div>

                {/* Entries for this topic */}
                <div className="space-y-8">
                  {groupEntries.map((entry, index) => {
                    const plainContent = stripHtml(entry.content || '');
                    const hasEnoughContent = plainContent.length > 100;

                    return (
                      <div key={entry.id} style={{ animationDelay: `${(groupIndex * 5 + index) * 80}ms` }} className="stagger-item relative group">
                        {/* Timeline Node */}
                        <div className={`absolute left-[-15px] md:left-[-29px] -translate-x-1/2 top-7 w-[12px] h-[12px] rounded-full border-2 transition-all duration-500 z-10 ${
                          isDarkMode
                            ? 'bg-[#0c0a09] border-stone-700 group-hover:border-stone-300 group-hover:scale-125'
                            : 'bg-white border-stone-300 group-hover:border-stone-900 group-hover:scale-125'
                        }`} />

                        {/* Card */}
                        <div
                          style={cardStyle}
                          className={`p-6 md:p-8 border transition-all duration-700 flex flex-col gap-3 relative ${
                            isDarkMode
                              ? 'border-transparent bg-stone-900/30 hover:border-stone-800/80 hover:bg-stone-900/60'
                              : 'border-transparent bg-stone-50/50 hover:border-stone-100 hover:bg-stone-50/90'
                          }`}>
                          {/* Date row */}
                          <div className="flex items-center flex-wrap gap-3 text-[10px] font-bold uppercase tracking-widest opacity-50">
                            <span className="flex items-center gap-1.5">
                              <Calendar size={12} />
                              {formatReadableDate(entry.date)}
                            </span>
                            {entry.readTime && (
                              <>
                                <span className="opacity-30">•</span>
                                <span className="flex items-center gap-1"><Clock size={11} />{entry.readTime}</span>
                              </>
                            )}
                          </div>

                          {/* Unified Note Content - collapsed via line-clamp, expands inline */}
                          <div className="relative">
                            <div
                              className={`prose prose-stone max-w-none leading-relaxed font-sans text-[1rem] ${
                                isDarkMode ? 'prose-invert' : ''
                              } ${expandedId === entry.id ? '' : 'line-clamp-3'}`}
                              dangerouslySetInnerHTML={{ __html: entry.content || '' }}
                            />
                            {/* Fade gradient at bottom when collapsed */}
                            {expandedId !== entry.id && hasEnoughContent && (
                              <div className={`absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t pointer-events-none ${
                                isDarkMode
                                  ? 'from-stone-900 via-stone-900/40 to-transparent'
                                  : 'from-stone-50 via-stone-50/40 to-transparent'
                              }`} />
                            )}
                          </div>

                          {/* Expand / Collapse Arrow */}
                          {hasEnoughContent && (
                            <div className="flex justify-end">
                              <button
                                onClick={() => toggleExpand(entry.id)}
                                className={`p-1.5 rounded-full border transition-all duration-300 hover:scale-110 ${
                                  isDarkMode
                                    ? 'border-stone-700 hover:border-stone-500 hover:bg-stone-800'
                                    : 'border-stone-200 hover:border-stone-400 hover:bg-stone-100'
                                } ${theme.text}`}
                                aria-label={expandedId === entry.id ? 'Collapse note' : 'Expand note'}
                              >
                                {expandedId === entry.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BookTimelineSection;
